import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ProductionWaste() {
  const { user } = useContext(AuthContext);
  const canApprove = ["owner", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/productions");
    setRows(r.data.filter((x) => x.status !== "CANCELLED"));
  }, []);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  const wasteRows = rows.flatMap((pr) => (pr.waste || []).map((w) => ({ ...w, prod_no: pr.prod_no, production_id: pr.production_id })));
  const rejectRows = rows.flatMap((pr) => (pr.packages || []).filter((p) => p.reject_dus > 0).map((p) => ({
    prod_no: pr.prod_no, product: pr.product_name, date: pr.date, package: p.seq, operator: p.employee_name,
    reject: p.reject_dus, reason: p.reject_reason, note: p.note,
  })));

  const act = async (pr, w, action) => {
    if (!window.confirm(`${action === "approve" ? "Setujui" : "Tolak"} waste ${w.name} ${num(w.qty)} ${w.unit} (${rupiah(w.value)})?${action === "reject" ? " Bahan otomatis kembali ke gudang." : ""}`)) return;
    try {
      await api.post(`/productions/${pr.production_id}/waste/${w.waste_id}/${action}`);
      toast.success(action === "approve" ? "Waste disetujui — stok produksi berkurang" : "Waste ditolak — bahan kembali ke gudang");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="page-production-waste" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Produksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Reject & Waste</h1>
        <p className="text-slate-500 text-sm mt-1">Reject produk dicatat per paket (tidak dibayar). Waste bahan butuh approval Owner/Manager sebelum mengurangi stok.</p>
      </div>

      <Card>
        <div className="px-4 pt-4 text-xs font-mono uppercase tracking-wider text-slate-500">Waste Bahan (approval)</div>
        <table className="w-full text-sm" data-testid="table-waste">
          <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
            <th className="py-2 px-4">Produksi</th><th>Bahan</th><th className="text-right">Qty</th><th className="text-right">Nilai</th>
            <th>Alasan</th><th>Dibuat</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {wasteRows.map((w) => (
              <tr key={w.waste_id} className="border-b" data-testid={`waste-row-${w.waste_id}`}>
                <td className="py-2 px-4 font-mono text-xs">{w.prod_no}</td>
                <td>{w.sku} · {w.name}</td>
                <td className="text-right font-mono">{num(w.qty)} {w.unit}</td>
                <td className="text-right font-mono">{rupiah(w.value)}</td>
                <td className="text-sm">{w.reason}</td>
                <td className="text-xs text-slate-500">{w.created_by}</td>
                <td>
                  {w.status === "PENDING" && <Badge className="bg-amber-100 text-amber-800 font-normal">Pending Approval</Badge>}
                  {w.status === "APPROVED" && <Badge className="bg-emerald-100 text-emerald-800 font-normal">Approved</Badge>}
                  {w.status === "REJECTED" && <Badge className="bg-rose-100 text-rose-800 font-normal">Rejected</Badge>}
                </td>
                <td className="whitespace-nowrap">
                  {w.status === "PENDING" && canApprove && (
                    <>
                      <Button size="sm" onClick={() => act({ production_id: w.production_id }, w, "approve")} data-testid={`waste-approve-${w.waste_id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Setujui</Button>
                      <Button size="sm" variant="ghost" onClick={() => act({ production_id: w.production_id }, w, "reject")} data-testid={`waste-reject-${w.waste_id}`}><XCircle className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {wasteRows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Belum ada waste tercatat</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card>
        <div className="px-4 pt-4 text-xs font-mono uppercase tracking-wider text-slate-500">Reject Produk per Paket (tidak dibayar)</div>
        <table className="w-full text-sm" data-testid="table-rejects">
          <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
            <th className="py-2 px-4">Tanggal</th><th>Produksi</th><th>Produk</th><th>Paket</th><th>Operator</th>
            <th className="text-right">Reject (dus)</th><th>Alasan</th><th>Keterangan</th>
          </tr></thead>
          <tbody>
            {rejectRows.map((r, i) => (
              <tr key={i} className="border-b">
                <td className="py-2 px-4">{formatDate(r.date)}</td>
                <td className="font-mono text-xs">{r.prod_no}</td>
                <td>{r.product}</td>
                <td className="font-mono">#{r.package}</td>
                <td>{r.operator}</td>
                <td className="text-right font-mono text-rose-700 font-bold">{num(r.reject)}</td>
                <td>{r.reason}</td>
                <td className="text-xs text-slate-500">{r.note || "—"}</td>
              </tr>
            ))}
            {rejectRows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Tidak ada reject</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
